import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Pagination, SortBy } from "@customTypes/index";
import { UnifiedScheduleItem, UnifiedStatus } from "@customTypes/schedule";

import { scheduleQKeys } from "./queryKeys";


const select = (res: any) => {
  const pagination: Pagination = {
    page: res.data.pagination.page,
    pageCount: res.data.pagination.page_count,
    pageSize: res.data.pagination.page_size,
    total: res.data.pagination.total,
  };

  if (res.data.data.length) {
    const newData: UnifiedScheduleItem[] = res.data.data.map((item: any) => ({
      id: item._id,
      type: item.type,
      patientName: item.patient_name,
      patientId: item.patient_id,
      phoneNumber: item.phone_number,
      doctorName: item.doctor_name,
      doctorId: item.doctor_id,
      service: item.service,
      category: item.category,
      date: new Date(item.date),
      appointmentDate: new Date(item.appointment_date),
      status: item.status as UnifiedStatus,
      billStatus: item.bill_status,
      createdAt: item.created_at ? new Date(item.created_at) : undefined,
      updatedAt: item.updated_at ? new Date(item.updated_at) : undefined,
      consultationId: item.consultation_id,
      cId: item.c_id,
      callChannelId: item.c_id ? `consultation_${item.c_id}` : "",
      callStatus: item.call_status ?? "not_started",
      originalAppointmentId: item.original_appointment_id,
      paymentLink: item.payment_link,
    }));

    return {
      newData,
      pagination,
    };
  }

  return {
    newData: [],
    pagination,
  };
};

interface QueryProps {
  isEnabled?: boolean;
  pagination?: Partial<Pagination>;
  search?: string;
  doctor?: string;
  status?: UnifiedStatus;
  category?: string;
  startDate?: Date;
  endDate?: Date;
  sortBy?: SortBy;
}

export const useGetSchedule = ({
  isEnabled = true,
  pagination,
  search = "",
  doctor = "",
  status,
  category,
  startDate,
  endDate,
  sortBy = "desc",
}: QueryProps) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: scheduleQKeys.allWithFilters({
      pagination,
      search,
      doctor,
      status,
      category,
      startDate,
      endDate,
      sortBy,
    }),
    queryFn: () => {
      return api.get("schedule/", {
        params: {
          search,
          doctor_assigned: doctor,
          status,
          service_type: category, // Mapping category to service_type backend param
          start_date: startDate?.toISOString(),
          end_date: endDate?.toISOString(),
          order_by: sortBy,
          page: pagination?.page,
          page_size: pagination?.pageSize,
        },
      });
    },
    select,
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes

    onError: (res: any) => {
      console.error("[From Get Unified Schedule]", res);
      handleErrors(res, "query");
    },
  });
};
